<template>
    <div
        ref="googleBtn"
        :class="{
            [$style.small_button]: props.size === 's',
            [$style.large_button]: props.size === 'l',
        }"
    />
</template>
<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { onMounted, ref, watch } from 'vue'

import { useGoogleAuthStore } from '@/store/google-auth-store'
import { useLoggerStore } from '@/store/logger'

const props = defineProps<{
    size: 's' | 'l'
}>()

const emit = defineEmits<{
    click: []
    finish: [isSuccess: boolean, errorMessage?: string]
}>()

const loggerStore = useLoggerStore()
const googleAuthStore = useGoogleAuthStore()
const { initialized } = storeToRefs(googleAuthStore)

const googleBtn = ref<HTMLElement>()

// Google draws the button 40px tall with 14px text and offers nothing bigger, so .large_button
// enlarges it with transform: scale(). The width asked from Google is divided by the same number,
// otherwise the enlarged button is wider than its row
const largeButtonScale = 1.1

const initSignIn = () => {
    if (!window.google || !googleBtn.value) {
        return
    }

    window.google.accounts.id.renderButton(googleBtn.value, {
        type: 'standard',
        text: 'signin_with',
        theme: 'outline',
        shape: 'pill',
        size: 'large',
        logo_alignment: 'left',
        // Google takes the width only here, so the large button is sized to the row it sits in:
        // on a phone the row is narrower than the scaled 320px and would cut the right end off
        width: props.size === 's' ? 292 : Math.min(320, Math.floor(googleBtn.value.clientWidth / largeButtonScale)),
        state: JSON.stringify({ returnURL: window.location.href }),
        click_listener: onClickHandler,
    })
}

function onClickHandler() {
    loggerStore.logInterfaceEvents({
        eventName: 'interface_click',
        interface_target: 'google-sign-in-button',
    })
    emit('click')
    googleAuthStore.setOnFinishCallback((isSuccess, errorMessage) => emit('finish', isSuccess, errorMessage))
}

watch(initialized, initSignIn)
onMounted(() => {
    if (initialized.value) {
        initSignIn()
    }
})
</script>
<style module>
.small_button,
.large_button {
    color-scheme: light;
    height: 44px;
    overflow: hidden;
}

/*
    Using transform because the button is inside an iframe and can't be styled directly;
    fixed height prevents jumping when the user's photo loads
*/
.small_button {
    transform: scale(0.85);
    transform-origin: left center;
    width: 292px;
}

.large_button {
    transform: scale(v-bind(largeButtonScale)) translateY(2px);
    transform-origin: left center;
}
</style>
